import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * Gift picker modal custom element.
 * Opens to let customers choose their free gift products.
 * Fetches products from a Shopify collection via section rendering API.
 */
class GiftPickerModal extends Component {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  #tier = 0;
  #maxSelections = 0;
  #selections = [];
  #products = [];
  #loadedCollection = null;
  #searchTimer = null;
  #title = null;
  #counterCurrent = null;
  #counterMax = null;
  #searchInput = null;
  #categoryFilter = null;
  #brandFilter = null;
  #productList = null;
  #confirmBtn = null;
  #closeBtn = null;
  #i18n = {};

  connectedCallback() {
    super.connectedCallback();

    // Load i18n strings from Liquid-generated JSON config
    try {
      const i18nEl = document.getElementById('hs-gift-picker-i18n');
      if (i18nEl) this.#i18n = JSON.parse(i18nEl.textContent);
    } catch (e) {}

    this.#dialog = this.querySelector('dialog');
    this.#title = this.querySelector('.hs-gift-picker__title');
    this.#counterCurrent = this.querySelector('.hs-gift-picker__counter-current');
    this.#counterMax = this.querySelector('.hs-gift-picker__counter-max');
    this.#searchInput = this.querySelector('.hs-gift-picker__search');
    this.#categoryFilter = this.querySelector('[data-filter="category"]');
    this.#brandFilter = this.querySelector('[data-filter="brand"]');
    this.#productList = this.querySelector('.hs-gift-picker__list');
    this.#confirmBtn = this.querySelector('.hs-gift-picker__confirm-btn');
    this.#closeBtn = this.querySelector('.hs-gift-picker__close');

    this.#closeBtn?.addEventListener('click', this.#close);
    this.#confirmBtn?.addEventListener('click', this.#confirm);
    this.#searchInput?.addEventListener('input', this.#onSearchInput);
    this.#categoryFilter?.addEventListener('change', this.#applyFilters);
    this.#brandFilter?.addEventListener('change', this.#applyFilters);
    this.#productList?.addEventListener('click', this.#onProductClick);

    this.#dialog?.addEventListener('close', this.#onDialogClose);

    // Close when clicking the backdrop area (outside the inner card).
    // Also stop propagation so the cart drawer's click-outside handler doesn't fire.
    this.#dialog?.addEventListener('click', (e) => {
      e.stopPropagation();
      // If the click target is the dialog itself (the backdrop overlay), close.
      if (e.target === this.#dialog) {
        this.#close();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#searchTimer);
  }

  /**
   * Public API — called by gift-threshold-bar.js
   * @param {number} tier - 1 or 2
   */
  open(tier) {
    // Re-query dialog in case DOM was morphed by section renderer
    this.#dialog = this.querySelector('dialog');

    this.#tier = tier;
    this.#selections = [];

    const tier1Qty = parseInt(this.dataset.tier1Qty, 10) || 2;
    const tier2Qty = parseInt(this.dataset.tier2Qty, 10) || 1;
    this.#maxSelections = tier === 1 ? tier1Qty : tier2Qty;

    if (this.#title) {
      this.#title.textContent =
        tier === 1
          ? (this.dataset.pickerTitleTier1 || 'Choose your free samples')
          : (this.dataset.pickerTitleTier2 || 'Choose your free 30ml perfume');
    }
    if (this.#counterMax) this.#counterMax.textContent = this.#maxSelections;
    if (this.#counterCurrent) this.#counterCurrent.textContent = '0';

    if (this.#searchInput) this.#searchInput.value = '';
    if (this.#categoryFilter) this.#categoryFilter.value = 'all';
    if (this.#brandFilter) this.#brandFilter.value = 'all';

    if (this.#loadedCollection !== tier) {
      this.#loadProducts(tier);
    } else {
      this.#renderProducts();
    }

    this.#updateConfirmBtn();
    // Use .show() instead of .showModal() to avoid nested modal dialog issues on mobile Safari.
    // The dialog is positioned as a full-screen overlay via CSS.
    this.#dialog?.show();
  }

  #loadProducts(tier) {
    if (this.#productList) {
      this.#productList.innerHTML =
        '<div class="hs-gift-picker__loading">' + (this.#i18n.loading || 'Loading products...') + '</div>';
    }

    try {
      const dataAttr = tier === 1 ? 'data-tier1-products' : 'data-tier2-products';
      const script = this.querySelector(`script[${dataAttr}]`);

      if (script && script.textContent.trim()) {
        this.#products = JSON.parse(script.textContent);
      } else {
        this.#products = [];
      }

      this.#loadedCollection = tier;
      this.#populateBrandFilter();
      this.#renderProducts();
    } catch {
      if (this.#productList) {
        this.#productList.innerHTML =
          '<p class="hs-gift-picker__empty">' + (this.#i18n.load_error || 'Could not load products. Please try again.') + '</p>';
      }
    }
  }

  #populateBrandFilter() {
    if (!this.#brandFilter) return;

    const brands = new Set();
    for (const p of this.#products) {
      if (p.brand) brands.add(p.brand);
    }

    const sorted = [...brands].sort((a, b) =>
      a.localeCompare(b, 'sv')
    );

    this.#brandFilter.innerHTML =
      '<option value="all">' + (this.#i18n.brand_filter_all || 'INSPIRED BY \u2013 ALL') + '</option>' +
      sorted
        .map(
          (b) =>
            `<option value="${this.#handleize(b)}">${this.#escapeHtml(b.toUpperCase())}</option>`
        )
        .join('');
  }

  #renderProducts() {
    if (!this.#productList) return;

    const searchTerm = (this.#searchInput?.value || '').toLowerCase().trim();
    const category = this.#categoryFilter?.value || 'all';
    const brand = this.#brandFilter?.value || 'all';

    let filtered = this.#products.filter((p) => {
      if (searchTerm && !p.search.includes(searchTerm)) return false;
      if (category !== 'all' && p.gender !== category) return false;
      if (brand !== 'all' && this.#handleize(p.brand) !== brand) return false;
      return true;
    });

    // Bestsellers first, then alphabetical
    filtered.sort((a, b) => {
      if (a.bestseller && !b.bestseller) return -1;
      if (!a.bestseller && b.bestseller) return 1;
      return a.title.localeCompare(b.title, 'sv');
    });

    if (filtered.length === 0) {
      this.#productList.innerHTML =
        '<p class="hs-gift-picker__empty">' + (this.#i18n.no_products || 'No products found') + '</p>';
      return;
    }

    this.#productList.innerHTML = filtered
      .map((p) => this.#renderRow(p))
      .join('');
  }

  #renderRow(product) {
    const isSelected = this.#selections.some(
      (s) => s.variantId === String(product.variantId)
    );

    const tags = [];
    if (product.bestseller) {
      tags.push(
        `<span class="hs-gift-picker__tag hs-gift-picker__tag--bestseller">${this.#i18n.tag_bestseller || 'BESTSELLER'}</span>`
      );
    }
    if (product.gender === 'him') {
      tags.push(
        `<span class="hs-gift-picker__tag hs-gift-picker__tag--gender">${this.#i18n.tag_him || 'HIM'}</span>`
      );
    } else if (product.gender === 'her') {
      tags.push(
        `<span class="hs-gift-picker__tag hs-gift-picker__tag--gender">${this.#i18n.tag_her || 'HER'}</span>`
      );
    } else if (product.gender === 'unisex') {
      tags.push(
        `<span class="hs-gift-picker__tag hs-gift-picker__tag--gender">${this.#i18n.tag_unisex || 'UNISEX'}</span>`
      );
    }

    const priceKr = this.#i18n.free_label || 'FREE';

    return `
      <div class="hs-gift-picker__row${isSelected ? ' hs-gift-picker__row--selected' : ''}"
           data-variant-id="${product.variantId}"
           data-title="${this.#escapeAttr(product.title)}"
           data-image="${this.#escapeAttr(product.image || '')}"
           role="listitem">
        <div class="hs-gift-picker__row-thumb">
          ${product.image ? `<img src="${product.image}" alt="" width="48" height="48" loading="lazy">` : ''}
        </div>
        <div class="hs-gift-picker__row-info">
          <span class="hs-gift-picker__row-name">${this.#escapeHtml(product.title)}</span>
          ${product.inspiredBy ? `<span class="hs-gift-picker__row-inspired">${this.#escapeHtml(/^insp\.?\s*(av|by)\b/i.test(product.inspiredBy) ? product.inspiredBy : (this.#i18n.inspired_by_prefix || 'INSP. BY') + ' ' + product.inspiredBy).toUpperCase()}</span>` : ''}
          ${tags.length ? `<div class="hs-gift-picker__row-tags">${tags.join('')}</div>` : ''}
        </div>
        <span class="hs-gift-picker__row-price">${priceKr}</span>
        <button class="hs-gift-picker__row-btn" type="button"
                aria-label="${isSelected ? (this.#i18n.remove_label || 'Remove') : (this.#i18n.add_label || 'Add')} ${this.#escapeAttr(product.title)}">
          ${isSelected ? '<svg viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' : '<svg viewBox="0 0 12 12" fill="none"><line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'}
        </button>
      </div>`;
  }

  #onProductClick = (e) => {
    const row = e.target.closest('.hs-gift-picker__row');
    if (!row) return;
    e.preventDefault();

    const variantId = row.dataset.variantId;
    const idx = this.#selections.findIndex(
      (s) => s.variantId === variantId
    );

    if (idx >= 0) {
      this.#selections.splice(idx, 1);
    } else {
      if (this.#selections.length >= this.#maxSelections) {
        // Replace oldest selection
        this.#selections.shift();
      }
      this.#selections.push({
        variantId,
        title: row.dataset.title,
        image: row.dataset.image,
      });
    }

    this.#updateSelectionUI();
    this.#updateConfirmBtn();
  };

  #updateSelectionUI() {
    const rows = this.#productList?.querySelectorAll(
      '.hs-gift-picker__row'
    );
    if (!rows) return;

    rows.forEach((row) => {
      const vid = row.dataset.variantId;
      const isSelected = this.#selections.some(
        (s) => s.variantId === vid
      );
      row.classList.toggle('hs-gift-picker__row--selected', isSelected);

      const btn = row.querySelector('.hs-gift-picker__row-btn');
      if (btn) {
        btn.innerHTML = isSelected
          ? '<svg viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : '<svg viewBox="0 0 12 12" fill="none"><line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        btn.setAttribute(
          'aria-label',
          `${isSelected ? (this.#i18n.remove_label || 'Remove') : (this.#i18n.add_label || 'Add')} ${row.dataset.title}`
        );
      }
    });

    if (this.#counterCurrent) {
      this.#counterCurrent.textContent = this.#selections.length;
    }
  }

  #updateConfirmBtn() {
    if (this.#confirmBtn) {
      const ready = this.#selections.length === this.#maxSelections;
      this.#confirmBtn.disabled = !ready;
      this.#confirmBtn.textContent = ready
        ? (this.#i18n.confirm_btn || 'Confirm selection')
        : (this.#i18n.select_more || 'Choose _COUNT_ more').replace('_COUNT_', this.#maxSelections - this.#selections.length);
    }
  }

  #confirm = async () => {
    if (this.#selections.length !== this.#maxSelections) return;

    this.#confirmBtn.disabled = true;
    this.#confirmBtn.textContent = this.#i18n.adding || 'Adding...';

    try {
      const items = this.#selections.map((s) => ({
        id: parseInt(s.variantId, 10),
        quantity: 1,
        properties: {
          _gift_tier: `tier${this.#tier}`,
        },
      }));

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Discount code is applied at checkout time (see gift-threshold-bar.js)
      // to prevent it from making non-gift 30ml variants free while browsing.

      this.#close();

      // Dispatch events to refresh cart UI
      document.dispatchEvent(new CartAddEvent());
      document.dispatchEvent(new CustomEvent('gift:selected'));
    } catch {
      this.#confirmBtn.disabled = false;
      this.#confirmBtn.textContent = this.#i18n.add_error || 'Could not add. Please try again.';
      setTimeout(() => {
        this.#updateConfirmBtn();
      }, 2000);
    }
  };

  #close = () => {
    this.#dialog?.close();
  };

  #onDialogClose = () => {
    // Reset state when dialog is closed via ESC or backdrop
  };

  #onSearchInput = () => {
    clearTimeout(this.#searchTimer);
    this.#searchTimer = setTimeout(() => this.#renderProducts(), 150);
  };

  #applyFilters = () => {
    this.#renderProducts();
  };

  #handleize(str) {
    return (str || '')
      .toLowerCase()
      .replace(/[^a-z0-9\u00e0-\u00ff]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  #escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  #escapeAttr(str) {
    return (str || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

if (!customElements.get('gift-picker-modal')) {
  customElements.define('gift-picker-modal', GiftPickerModal);
}
